import React, { useEffect, useMemo, useState } from 'react';
import { Button, message, Table } from 'antd';
import type { TableColumnsType } from 'antd';
import AccountFormDrawer from './components/AccountFormDrawer';
import FilterArea, { DEFAULT_FILTER_OPTIONS } from './components/FilterArea';
import { GET_ACCOUNT_LISTING_FOR_PICKUP, GetAccountListingForPickupResponse } from './graphql/queries/getAccountListingForPickup';
import { useQuery } from '@apollo/client';
import { AccountType } from 'graphql/types/AccountType';
import { getAccountPickupDriverByAccountId } from '../../../graphql/queries/getAccountPickupDriverByAccountId';
import { AccountFormData, useAccountApis } from 'hooks/useAccountApis';
import { FilterOptions } from './components/FilterArea';
import { useWarehouseContext } from "../../../contexts/WarehouseContext";
import { showError } from 'utils/exceptionUtils';
import { useTranslation } from 'react-i18next';
import TablePaginationBar from '../../../components/TablePaginationBar';


const AccountPage: React.FC = () => {
    const { createAccount, updateAccount } = useAccountApis();
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState<AccountFormData | null>(null);
    const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');
    const [tempFilters, setTempFilters] = useState<FilterOptions>(DEFAULT_FILTER_OPTIONS);
    const [filterOptions, setFilterOptions] = useState<FilterOptions>(DEFAULT_FILTER_OPTIONS);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const { currentWarehouse: warehouse } = useWarehouseContext();

    const { data: allAccounts, loading: allAccountsLoading, error: allAccountsError, refetch: allAccountsRefetch } = useQuery<GetAccountListingForPickupResponse>(GET_ACCOUNT_LISTING_FOR_PICKUP, {
        variables: {
            warehouseId: warehouse?.id
        },
        skip: !warehouse?.id
    });

    const refetch = () => {
        allAccountsRefetch();
    }

    // Show new account drawer
    const showNewAccountDrawer = () => {
        setSelectedAccount(null);
        setDrawerMode('create');
        setDrawerVisible(true);
    };
    const { t } = useTranslation();
    // Show account details
    const showAccountDetail = async (account: AccountType) => {
        console.log('showAccountDetail', account);
        // Convert AccountData to AccountFormData
        const formData: AccountFormData = {
            id: account.id,
            phone: {
                countryCode: account.countryCode,
                phoneNumber: account.phone,
            },
            name: account.name,
            email: account.email,
            role: account.role.name,
            status: account.status,
            isOnDuty: true
        };

        const role = account.role.name;
        if (role === 'PICKUP_DRIVER') {
            const pickupDriver = await getAccountPickupDriverByAccountId(account.id);
            formData.warehouseId = pickupDriver.warehouseId;
            formData.vehicleId = pickupDriver.vehicleId;
            formData.workWeekdays = JSON.parse(pickupDriver.workWeekdaysJson || '[1,2,3,4,5]');
            formData.workStartTime = pickupDriver.workStartTime;
            formData.workEndTime = pickupDriver.workEndTime;
            formData.responsibleZipcodes = pickupDriver.responsibleZipcodeListJson ? 
                JSON.parse(pickupDriver.responsibleZipcodeListJson) : [];
        } else {
            message.error(t('pickup.account.messages.unsupportedRole', {role: role}) || '');
            console.error(`Unsupported role: ${role}`); 
            showError(t('pickup.account.messages.unsupportedRole', {role: role}) || '');
            return;
        }

        setSelectedAccount(formData);
        setDrawerMode('edit');
        setDrawerVisible(true);
    };

    // Handle account form submission (create or update)
    const handleAccountSubmit = async (values: AccountFormData) => {
        if (drawerMode === 'create') {
            try {
                console.log('New account submitted:', values);
                const result = await createAccount(values);
                if (!result) {
                    return;
                }
                setDrawerVisible(false);
                message.success(t('pickup.account.messages.personnelCreatedSuccessfully') || '');
                refetch();
            } catch (error) {
                console.error('Error creating account:', error);
                showError(error);
            }
        } else {
            try {
                console.log('Personnel updated:', values);
                const result = await updateAccount(values);
                if (!result) {
                    return;
                }
                setDrawerVisible(false);
                message.success(t('pickup.account.messages.personnelUpdatedSuccessfully') || '');
                refetch();
            } catch (error) {
                console.error('Error updating account:', error);
                showError(error);
            }
        }
    };

    // Table column definitions
    const columns: TableColumnsType<AccountType> = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            align: 'left' as const,
            ellipsis: false,
            render: (text, record) => (
                <Button
                    type="link"
                    onClick={() => showAccountDetail(record)}
                    style={{ paddingLeft: 0 }}
                >
                    <span style={{ whiteSpace: 'nowrap' }}>{text}</span>
                </Button>
            ),
        },
        {
            title: t('pickup.account.columns.name'),
            dataIndex: 'name',
            key: 'name',
            align: 'left' as const,
            ellipsis: false,
            render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
        },
        {
            title: t('pickup.account.columns.phone'),
            dataIndex: 'phone',
            key: 'phone',
            align: 'left' as const,
            ellipsis: false,
            render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
        },
        {
            title: t('pickup.account.columns.role'),
            dataIndex: ['role', 'name'],
            key: 'role.name',
            align: 'left' as const,
            ellipsis: false,
            render: (_, record) => {
                const text = t(`enum.role.${record.role.name}`);
                return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            },
        },
        {
            title: t('pickup.account.columns.status'),
            dataIndex: 'status',
            key: 'status',
            align: 'left' as const,
            ellipsis: false,
            render: (status: number) => {
                const color = status === 1 ? 'text-green-600' : 'text-gray-400';
                const text = status === 1 ? t('enum.activeStatus.active') : t('enum.activeStatus.suspended');
                return <span className={color} style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            },
        },
    ];

    const filteredData = React.useMemo(() => {
        return allAccounts?.allAccounts
            .filter((account) => filterOptions.role === 'all' || filterOptions.role === account.role.name)
            .filter((account) => filterOptions.status === 2 || filterOptions.status === account.status)
            .filter((account) => filterOptions.id === '' || account.id.toString().includes(filterOptions.id))
            .filter((account) => filterOptions.name === '' || account.name.toLowerCase().includes(filterOptions.name.toLowerCase()))
            .filter((account) => filterOptions.phone === '' || account.phone.includes(filterOptions.phone)) || [];
    }, [allAccounts?.allAccounts, filterOptions]);

    const pagedFilteredData = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        const end = start + pageSize;
        return filteredData.slice(start, end);
    }, [filteredData, currentPage, pageSize]);

    useEffect(() => {
        // 仓库/筛选变化后，重置到第一页
        setCurrentPage(1);
    }, [warehouse?.id, filterOptions]);

    useEffect(() => {
        // 过滤后数据变少时，避免 currentPage 越界
        const maxPage = Math.max(1, Math.ceil(filteredData.length / pageSize));
        if (currentPage > maxPage) {
            setCurrentPage(1);
        }
    }, [filteredData.length, pageSize, currentPage]);

    const handleFilter = () => {
        setFilterOptions(tempFilters);
        setCurrentPage(1);
    };

    const handleResetFilters = () => {
        setTempFilters(DEFAULT_FILTER_OPTIONS);
        setFilterOptions(DEFAULT_FILTER_OPTIONS);
        setCurrentPage(1);
    };

    return (
            <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
                {/* Filter area */}
                <FilterArea
                    filters={tempFilters}
                    setFilters={setTempFilters}
                />

                {/* 批量操作区域 */}
                <div className="flex justify-end items-center mb-3 pl-0">
                    <div className="flex items-center gap-2">
                        <Button
                            type="primary"
                            onClick={showNewAccountDrawer}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('pickup.account.buttons.new')}</span>
                        </Button>
                        <Button
                            type="primary"
                            onClick={handleFilter}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('pickup.account.buttons.filter')}</span>
                        </Button>
                        <Button
                            onClick={handleResetFilters}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('pickup.account.buttons.reset')}</span>
                        </Button>
                    </div>
                </div>

                {/* Personnel table */}
                <Table
                    rowKey="id"
                    loading={allAccountsLoading}
                    columns={columns}
                    dataSource={pagedFilteredData}
                    pagination={false}
                    scroll={{x: 'max-content'}}
                    size="middle"
                    tableLayout="auto"
                 className="mb-4"/>

                {/* 分页 */}
                <TablePaginationBar
                    total={filteredData.length}
                    current={currentPage}
                    pageSize={pageSize}
                    onChange={(page, size) => {
                        setCurrentPage(page);
                        if (size && size !== pageSize) {
                            setPageSize(size);
                        }
                    }}
                    onShowSizeChange={(_, size) => {
                        setCurrentPage(1);
                        setPageSize(size);
                    }}
                    pageSizeOptions={['10', '20', '50', '100']}
                />

                {/* Personnel form drawer (shared for create/edit) */}
                <AccountFormDrawer
                    visible={drawerVisible}
                    onClose={() => setDrawerVisible(false)}
                    onSubmit={handleAccountSubmit}
                    account={selectedAccount}
                    mode={drawerMode}
                />
            </div>
    );
};

export default AccountPage;